/**
 * Task ordering helpers for the order-detail Tasks panel.
 *
 * <p>
 * `sequence` is the single source of truth for the order tasks execute
 * in — not planned date/time. That's deliberate: `plannedDate` is
 * nullable, `timeMode` of `AM` / `PM` / `Undefined` describes an
 * interval rather than an instant, and downstream consumers read real
 * meaning off the order (the bookings cockpit picks its ferry
 * insertion point from the first delivery-side task; the booking
 * worklist derives origin / destination from the first and last task).
 * Auto-sorting by date would let one mistyped date silently put a
 * delivery above its collection.
 * </p>
 *
 * <p>
 * So dates are advisory: {@link findChronologyConflicts} flags rows
 * that look out of order and the operator resolves them by dragging.
 * Nothing here reorders anything on its own.
 * </p>
 */
import type { OrderTaskSequenceInput } from '@my/tms-api';
import type { MockTask } from './types';

/** Minutes in a day — the unit chronology windows are expressed in. */
const MINUTES_PER_DAY = 24 * 60;

/** Midday, the AM/PM boundary (`TaskTimeMode.AM` = before midday). */
const MIDDAY = 12 * 60;

/**
 * Move the item at `from` to `to`, returning a new array. Out-of-range
 * or no-op moves return the input untouched so callers can skip the
 * write without a separate guard.
 */
export function moveTask<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to) return items.slice();
  if (from < 0 || from >= items.length) return items.slice();
  if (to < 0 || to >= items.length) return items.slice();
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/**
 * Renumber a task list to contiguous `1..n` for the resequence
 * endpoint, which requires an entry for every task on the order with
 * distinct sequences.
 *
 * <p>
 * Tasks the operator didn't move get renumbered too. That's harmless —
 * the `(OrderId, Sequence)` index is non-unique, gaps are legal, and
 * every consumer reads relative order rather than specific values.
 * </p>
 */
export function buildResequenceItems(tasks: readonly MockTask[]): OrderTaskSequenceInput[] {
  return tasks.map((task, index) => ({ taskId: task.id, sequence: index + 1 }));
}

/** `HH:mm` / `HH:mm:ss` → minutes from midnight; null when unparseable. */
function parseMinutes(time: string | undefined): number | null {
  if (!time) return null;
  const parts = time.split(':');
  const hours = Number(parts[0]);
  const minutes = Number(parts[1] ?? '0');
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/** `yyyy-MM-dd` → whole-day ordinal; null when unparseable. */
function parseDayOrdinal(date: string | undefined): number | null {
  if (!date) return null;
  const [y, m, d] = date.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const ms = Date.UTC(y!, m! - 1, d!);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 86_400_000);
}

/**
 * The span of absolute minutes a task could be executed in, given how
 * precisely its time is specified. Booking halves always carry an
 * exact sail / dock time and leave `timeMode` null, so they're treated
 * as `Exact` when a time is present.
 */
function plannedWindow(task: MockTask): { start: number; end: number } | null {
  const day = parseDayOrdinal(task.plannedDate);
  // No date means no constraint to check against — an undated task is
  // neither flagged nor used to flag others.
  if (day === null) return null;

  const dayStart = day * MINUTES_PER_DAY;

  if (task.timeMode === 'AM') return { start: dayStart, end: dayStart + MIDDAY - 1 };
  if (task.timeMode === 'PM') {
    return { start: dayStart + MIDDAY, end: dayStart + MINUTES_PER_DAY - 1 };
  }

  // A time present pins the task to an instant. That covers `Exact`,
  // and also booking halves — the BE leaves their `timeMode` null (the
  // adapter maps that to `Undefined`) but they always carry a precise
  // sail / dock time. `PickupDeliveryForm` nulls `plannedTime` whenever
  // the mode isn't `Exact`, so a non-Exact row with a time is a
  // crossing, not stale data.
  const exact = parseMinutes(task.plannedTime);
  if (exact !== null) return { start: dayStart + exact, end: dayStart + exact };

  // `Undefined` (BE `Any`), or `Exact` on a half-filled form: the task
  // can happen any time that day, so it constrains nothing.
  return { start: dayStart, end: dayStart + MINUTES_PER_DAY - 1 };
}

/**
 * Task ids whose planned window closes before an earlier task in the
 * list can even start — i.e. rows that can't happen in the position
 * they're sitting in.
 *
 * <p>
 * Compared against the running latest start rather than just the
 * previous row, so a task dragged several positions too early is still
 * caught. A window that merely overlaps is fine: an `AM` task sitting
 * under an `Undefined` one on the same day is perfectly executable,
 * and flagging it would make the warning meaningless.
 * </p>
 */
export function findChronologyConflicts(tasks: readonly MockTask[]): Set<string> {
  const conflicts = new Set<string>();
  let latestStart: number | null = null;

  for (const task of tasks) {
    const window = plannedWindow(task);
    if (!window) continue;

    if (latestStart !== null && window.end < latestStart) {
      conflicts.add(task.id);
    }
    latestStart = latestStart === null ? window.start : Math.max(latestStart, window.start);
  }

  return conflicts;
}
